import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import {
  SvgAdd,
  SvgArrowLeft,
  SvgClose,
  SvgTrash,
} from '@actual-app/components/icons/v1';
import { Input } from '@actual-app/components/input';
import { Menu } from '@actual-app/components/menu';
import { Select } from '@actual-app/components/select';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type {
  AccountEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

import { FinancialText } from '#components/FinancialText';
import { FormLabel } from '#components/forms';
import { AmountInput } from '#components/util/AmountInput';
import { useFormat } from '#hooks/useFormat';
import { useSheetValue } from '#hooks/useSheetValue';
import type {
  PlanningBudgetCategorySummary,
  PlanningGoal,
  PlanningGoalAllocation,
  PlanningGoalKind,
} from '#planning/types';
import { useLivePlanningBudgetCategorySummary } from '#planning/useLivePlanningBudgetCategorySummary';
import {
  buildPlanningGoalProjection,
  getPlanningGoalLeftToSave,
  getRemainingPayoffBalance,
  getPlanningGoalRecommendation,
  getPlanningGoalSavedAmount,
} from '#planning/utils';
import { accountBalance, selectedAccountsBalance } from '#spreadsheet/bindings';

import {
  getPlanningGoalIcon,
  getPlanningGoalLabel,
  planningGoalKinds,
} from './goalMetadata';

type PlanningGoalWizardProps = {
  open: boolean;
  goal: PlanningGoal | null;
  accounts: AccountEntity[];
  categoryGroups: CategoryGroupEntity[];
  budgetCategoriesById: Map<string, PlanningBudgetCategorySummary>;
  currentMonth: string;
  onClose: () => void;
  onSave: (goal: PlanningGoal) => void;
};

type PlanningGoalDraftAllocation = {
  id: string;
  accountId: string;
};

type PlanningGoalDraft = {
  id: string | null;
  kind: PlanningGoalKind | null;
  name: string;
  targetAmount: number;
  payoffAccountId: string;
  savedAmountAtLastSync: number;
  annualPercentageRate: string;
  categoryId: string;
  hasTargetDate: boolean;
  targetMonth: string;
  allocations: PlanningGoalDraftAllocation[];
  createdAt: string | null;
};

export function PlanningGoalWizard({
  open,
  goal,
  accounts,
  categoryGroups,
  budgetCategoriesById,
  currentMonth,
  onClose,
  onSave,
}: PlanningGoalWizardProps) {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const format = useFormat();
  const formatPlanningAmount = (value: number) => format.forEdit(value);
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [draft, setDraft] = useState<PlanningGoalDraft>(() =>
    createPlanningGoalDraft({
      goal: null,
      currentMonth,
    }),
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setDraft(
      createPlanningGoalDraft({
        goal,
        currentMonth,
        categorySummary:
          goal?.categoryId != null
            ? budgetCategoriesById.get(goal.categoryId)
            : undefined,
      }),
    );
    setStep(goal ? 1 : 0);
  }, [currentMonth, goal, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  const categoryOptions = useMemo(() => {
    const options: Array<readonly [string, string] | typeof Menu.line> = [];
    const expenseCategoryGroups = categoryGroups.filter(
      categoryGroup => !categoryGroup.is_income,
    );

    expenseCategoryGroups.forEach((categoryGroup, groupIndex) => {
      (categoryGroup.categories ?? [])
        .filter(category => !category.hidden)
        .forEach(category => {
          options.push([
            category.id,
            `${categoryGroup.name} / ${category.name}`,
          ]);
        });

      if (groupIndex < expenseCategoryGroups.length - 1) {
        options.push(Menu.line);
      }
    });

    return options;
  }, [categoryGroups]);

  const accountsById = useMemo(
    () => new Map(accounts.map(account => [account.id, account])),
    [accounts],
  );
  const activeAccountOptions = useMemo(
    () =>
      accounts
        .filter(account => !account.closed)
        .map(account => [account.id, account.name] as const),
    [accounts],
  );

  const annualPercentageRate = parseAnnualPercentageRate(
    draft.annualPercentageRate,
  );
  const isLoanPayoffGoal = draft.kind === 'loan-payoff';
  const linkedPayoffAccountBalanceValue = useSheetValue<
    'account',
    `balance-set-${string}`
  >(
    selectedAccountsBalance(
      draft.payoffAccountId ? [draft.payoffAccountId] : [],
    ),
  );
  const linkedPayoffAccountBalance = draft.payoffAccountId
    ? linkedPayoffAccountBalanceValue
    : null;
  const fallbackLinkedBudgetCategory = draft.categoryId
    ? budgetCategoriesById.get(draft.categoryId)
    : undefined;
  const linkedBudgetCategory = useLivePlanningBudgetCategorySummary({
    categoryId: draft.categoryId || null,
    currentMonth,
    fallbackSummary: fallbackLinkedBudgetCategory,
  });
  const previewGoal = useMemo(
    () =>
      draft.kind
        ? ({
            id: draft.id ?? 'preview-goal',
            kind: draft.kind,
            name: draft.name.trim(),
            targetAmount: draft.targetAmount,
            targetMonth: draft.hasTargetDate ? draft.targetMonth : null,
            annualPercentageRate,
            categoryId: draft.categoryId || null,
            payoffAccountId: draft.payoffAccountId || null,
            savedAmountAtLastSync: draft.savedAmountAtLastSync,
            categoryBalanceAtLastSync: linkedBudgetCategory?.balance ?? null,
            allocations: draft.allocations
              .filter(allocation => allocation.accountId)
              .map(
                allocation =>
                  ({
                    id: allocation.id,
                    accountId: allocation.accountId,
                    amount: 0,
                  }) satisfies PlanningGoalAllocation,
              ),
            createdAt: draft.createdAt ?? new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } satisfies PlanningGoal)
        : null,
    [
      annualPercentageRate,
      draft.allocations,
      draft.categoryId,
      draft.createdAt,
      draft.hasTargetDate,
      draft.id,
      draft.kind,
      draft.name,
      draft.payoffAccountId,
      draft.savedAmountAtLastSync,
      draft.targetAmount,
      draft.targetMonth,
      fallbackLinkedBudgetCategory,
      linkedBudgetCategory,
    ],
  );
  const goalRecommendation = previewGoal
    ? getPlanningGoalRecommendation({
        goal: previewGoal,
        currentMonth,
        categorySummary: linkedBudgetCategory,
        payoffAccountBalance: linkedPayoffAccountBalance,
      })
    : null;
  const savedAmount = previewGoal
    ? getPlanningGoalSavedAmount(
        previewGoal,
        linkedBudgetCategory,
        linkedPayoffAccountBalance,
      )
    : 0;
  const leftToSave = previewGoal
    ? getPlanningGoalLeftToSave(
        previewGoal,
        linkedBudgetCategory,
        linkedPayoffAccountBalance,
      )
    : 0;
  const projection = buildPlanningGoalProjection({
    goalKind: draft.kind ?? 'custom',
    currentAmount: savedAmount,
    targetAmount: draft.targetAmount,
    startMonth: currentMonth,
    targetMonth: draft.hasTargetDate ? draft.targetMonth : null,
    annualPercentageRate,
  });

  const canContinueFromType = !!draft.kind;
  const canContinueFromDetails =
    !!draft.kind &&
    draft.name.trim().length > 0 &&
    draft.targetAmount > 0 &&
    draft.categoryId.length > 0;
  const canSave =
    canContinueFromDetails &&
    (!draft.hasTargetDate || draft.targetMonth.length > 0);

  if (!open) {
    return null;
  }

  return (
    <View
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3100,
        backgroundColor: 'rgba(0, 0, 0, 0.48)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isNarrowWidth ? 12 : 20,
      }}
    >
      <View
        onClick={event => event.stopPropagation()}
        style={{
          width: 'min(980px, 100%)',
          maxHeight: 'calc(100vh - 32px)',
          backgroundColor: theme.modalBackground,
          borderRadius: 8,
          overflow: 'hidden',
          color: theme.pageText,
          ...styles.shadowLarge,
        }}
      >
        <View
          style={{
            padding: isNarrowWidth ? 14 : 18,
            borderBottom: `1px solid ${theme.tableBorder}`,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Button
            variant="bare"
            aria-label={t('Back')}
            isDisabled={step === 0}
            onPress={() =>
              setStep(currentStep => Math.max(0, currentStep - 1) as 0 | 1 | 2)
            }
            style={{
              padding: 6,
              visibility: step === 0 ? 'hidden' : 'visible',
            }}
          >
            <SvgArrowLeft style={{ width: 18, height: 18 }} />
          </Button>

          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                display: 'block',
                fontSize: isNarrowWidth ? 24 : 28,
                lineHeight: '1.1em',
                fontWeight: 700,
              }}
            >
              {goal ? t('Edit goal') : t('Create a goal')}
            </Text>
            <Text
              style={{
                display: 'block',
                marginTop: 4,
                color: theme.pageTextSubdued,
              }}
            >
              {step === 0
                ? t('What are you planning for?')
                : step === 1
                  ? t('Tell us about your goal.')
                  : t('Choose a target date and monthly pace.')}
            </Text>
          </View>

          <Button
            variant="bare"
            aria-label={t('Close')}
            onPress={onClose}
            style={{ padding: 6 }}
          >
            <SvgClose style={{ width: 18, height: 18 }} />
          </Button>
        </View>

        <View
          style={{
            padding: isNarrowWidth ? 16 : 20,
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 170px)',
          }}
        >
          {step === 0 ? (
            <GoalTypeStep
              selectedKind={draft.kind}
              onSelect={kind => {
                setDraft(currentDraft => ({
                  ...currentDraft,
                  kind,
                  name:
                    currentDraft.name.trim().length === 0
                      ? getPlanningGoalLabel(kind, t)
                      : currentDraft.name,
                }));
              }}
            />
          ) : null}

          {step === 1 ? (
            <GoalDetailsStep
              accounts={accounts}
              accountsById={accountsById}
              activeAccountOptions={activeAccountOptions}
              categoryOptions={categoryOptions}
              draft={draft}
              formatPlanningAmount={formatPlanningAmount}
              isLoanPayoffGoal={isLoanPayoffGoal}
              linkedBudgetCategory={linkedBudgetCategory}
              linkedPayoffAccountBalance={linkedPayoffAccountBalance}
              onChangeDraft={setDraft}
              savedAmount={savedAmount}
            />
          ) : null}

          {step === 2 ? (
            <GoalTimelineStep
              annualPercentageRate={annualPercentageRate}
              currentMonth={currentMonth}
              draft={draft}
              formatPlanningAmount={formatPlanningAmount}
              goalRecommendation={goalRecommendation}
              isLoanPayoffGoal={isLoanPayoffGoal}
              onChangeDraft={setDraft}
              projection={projection}
              savedAmount={savedAmount}
              leftToSave={leftToSave}
            />
          ) : null}
        </View>

        <View
          style={{
            padding: isNarrowWidth ? 14 : 18,
            borderTop: `1px solid ${theme.tableBorder}`,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <Text style={{ color: theme.pageTextSubdued }}>
            {t('Step {{step}} of 3', { step: step + 1 })}
          </Text>

          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Button variant="normal" onPress={onClose}>
              {t('Cancel')}
            </Button>
            {step < 2 ? (
              <Button
                variant="primary"
                isDisabled={
                  step === 0 ? !canContinueFromType : !canContinueFromDetails
                }
                onPress={() =>
                  setStep(
                    currentStep => Math.min(2, currentStep + 1) as 0 | 1 | 2,
                  )
                }
              >
                {t('Continue')}
              </Button>
            ) : (
              <Button
                variant="primary"
                isDisabled={!canSave || !draft.kind}
                onPress={() => {
                  if (!draft.kind || !canSave) {
                    return;
                  }

                  const now = new Date().toISOString();
                  onSave({
                    id: draft.id ?? crypto.randomUUID(),
                    kind: draft.kind,
                    name: draft.name.trim(),
                    targetAmount: draft.targetAmount,
                    targetMonth: draft.hasTargetDate ? draft.targetMonth : null,
                    annualPercentageRate,
                    categoryId: draft.categoryId,
                    payoffAccountId: draft.payoffAccountId || null,
                    savedAmountAtLastSync:
                      isLoanPayoffGoal && draft.payoffAccountId
                        ? savedAmount
                        : draft.savedAmountAtLastSync,
                    categoryBalanceAtLastSync:
                      linkedBudgetCategory?.balance ?? null,
                    allocations: draft.allocations
                      .filter(allocation => allocation.accountId)
                      .map(
                        allocation =>
                          ({
                            id: allocation.id,
                            accountId: allocation.accountId,
                            amount: 0,
                          }) satisfies PlanningGoalAllocation,
                      ),
                    createdAt: draft.createdAt ?? now,
                    updatedAt: now,
                  });
                  onClose();
                }}
              >
                {goal ? t('Save goal') : t('Create goal')}
              </Button>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}

function GoalTypeStep({
  selectedKind,
  onSelect,
}: {
  selectedKind: PlanningGoalKind | null;
  onSelect: (kind: PlanningGoalKind) => void;
}) {
  const { t } = useTranslation();

  return (
    <View
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: 14,
      }}
    >
      {planningGoalKinds.map(kind => {
        const Icon = getPlanningGoalIcon(kind);
        const isSelected = selectedKind === kind;

        return (
          <Button
            key={kind}
            variant={isSelected ? 'menuSelected' : 'menu'}
            onPress={() => onSelect(kind)}
            style={{
              width: '100%',
              minHeight: 152,
              padding: 18,
            }}
          >
            <View
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                gap: 12,
              }}
            >
              <View
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isSelected
                    ? theme.noticeBackground
                    : theme.noticeBackgroundLight,
                  color: isSelected ? theme.noticeTextDark : theme.reportsBlue,
                }}
              >
                <Icon style={{ width: 28, height: 28, color: 'inherit' }} />
              </View>
              <Text
                style={{
                  display: 'block',
                  fontSize: 22,
                  lineHeight: '1.2em',
                  fontWeight: 600,
                  textAlign: 'center',
                  overflowWrap: 'anywhere',
                }}
              >
                {getPlanningGoalLabel(kind, t)}
              </Text>
            </View>
          </Button>
        );
      })}
    </View>
  );
}

function GoalDetailsStep({
  accounts,
  accountsById,
  activeAccountOptions,
  categoryOptions,
  draft,
  formatPlanningAmount,
  isLoanPayoffGoal,
  linkedBudgetCategory,
  linkedPayoffAccountBalance,
  onChangeDraft,
  savedAmount,
}: {
  accounts: AccountEntity[];
  accountsById: Map<string, AccountEntity>;
  activeAccountOptions: Array<readonly [string, string]>;
  categoryOptions: Array<readonly [string, string] | typeof Menu.line>;
  draft: PlanningGoalDraft;
  formatPlanningAmount: (value: number) => string;
  isLoanPayoffGoal: boolean;
  linkedBudgetCategory?: PlanningBudgetCategorySummary;
  linkedPayoffAccountBalance: number | null;
  onChangeDraft: React.Dispatch<React.SetStateAction<PlanningGoalDraft>>;
  savedAmount: number;
}) {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const selectedAccountIds = draft.allocations
    .map(allocation => allocation.accountId)
    .filter(accountId => accountId.length > 0);
  const linkedAccountsBalance = Math.max(
    0,
    useSheetValue<'account', `balance-set-${string}`>(
      selectedAccountsBalance(selectedAccountIds),
    ) ?? 0,
  );
  const remainingPayoffBalance = getRemainingPayoffBalance(
    linkedPayoffAccountBalance,
  );
  const accountCoverageDifference = Math.max(
    0,
    savedAmount - linkedAccountsBalance,
  );

  return (
    <View>
      <View
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrowWidth
            ? '1fr'
            : 'repeat(2, minmax(0, 1fr))',
          gap: 16,
        }}
      >
        <WizardField label={t('Goal name')}>
          <Input
            value={draft.name}
            placeholder={t('Emergency Fund')}
            onChangeValue={value =>
              onChangeDraft(currentDraft => ({
                ...currentDraft,
                name: value,
              }))
            }
          />
        </WizardField>

        <WizardField
          label={isLoanPayoffGoal ? t('Payoff target') : t('Goal amount')}
        >
          <AmountInput
            value={draft.targetAmount}
            sign="+"
            onUpdate={amount =>
              onChangeDraft(currentDraft => ({
                ...currentDraft,
                targetAmount: Math.max(0, amount),
              }))
            }
          />
        </WizardField>

        <WizardField label={t('APR')}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Input
              value={draft.annualPercentageRate}
              inputMode="decimal"
              placeholder="0"
              onChangeValue={value => {
                if (/^\d*\.?\d*$/.test(value)) {
                  onChangeDraft(currentDraft => ({
                    ...currentDraft,
                    annualPercentageRate: value,
                  }));
                }
              }}
            />
            <Text style={{ color: theme.pageTextSubdued }}>%</Text>
          </View>
        </WizardField>

        <WizardField label={t('Budget category')}>
          <Select
            value={draft.categoryId}
            defaultLabel={t('Select a category')}
            options={categoryOptions}
            onChange={categoryId =>
              onChangeDraft(currentDraft => ({
                ...currentDraft,
                categoryId,
              }))
            }
            style={{ width: '100%' }}
          />
        </WizardField>

        {isLoanPayoffGoal ? (
          <WizardField label={t('Loan account')}>
            <Select
              value={draft.payoffAccountId}
              defaultLabel={t('Select a loan account')}
              options={activeAccountOptions}
              disabledKeys={selectedAccountIds}
              onChange={payoffAccountId =>
                onChangeDraft(currentDraft => ({
                  ...currentDraft,
                  payoffAccountId,
                }))
              }
              style={{ width: '100%' }}
            />
          </WizardField>
        ) : null}
      </View>

      <View
        style={{
          marginTop: 24,
          paddingTop: 18,
          borderTop: `1px solid ${theme.tableBorder}`,
        }}
      >
        <Text
          style={{
            display: 'block',
            fontSize: 20,
            fontWeight: 600,
          }}
        >
          {isLoanPayoffGoal ? t('Paid off so far') : t('Saved so far')}
        </Text>
        <Text
          style={{
            display: 'block',
            marginTop: 6,
            color: theme.pageTextSubdued,
          }}
        >
          {isLoanPayoffGoal
            ? t(
                'Link the loan account if you want payoff progress to stay synced to the live debt balance, then choose the cash accounts that will fund the payments.',
              )
            : t(
                'Enter what is already set aside today, then choose the accounts where this money lives. After you save, this goal follows the linked budget category automatically.',
              )}
        </Text>

        <View
          style={{
            marginTop: 16,
            display: 'grid',
            gridTemplateColumns: isNarrowWidth
              ? '1fr'
              : 'minmax(220px, 280px) minmax(0, 1fr)',
            gap: 16,
            alignItems: 'end',
          }}
        >
          <WizardField
            label={
              isLoanPayoffGoal ? t('Already paid off') : t('Already saved')
            }
          >
            {isLoanPayoffGoal && draft.payoffAccountId ? (
              <View
                style={{
                  minHeight: 44,
                  justifyContent: 'center',
                  padding: '10px 12px',
                  borderRadius: 6,
                  backgroundColor: theme.tableBackground,
                  border: `1px solid ${theme.tableBorder}`,
                }}
              >
                <FinancialText style={{ fontWeight: 600 }}>
                  {formatPlanningAmount(savedAmount)}
                </FinancialText>
              </View>
            ) : (
              <AmountInput
                value={draft.savedAmountAtLastSync}
                sign="+"
                onUpdate={amount =>
                  onChangeDraft(currentDraft => ({
                    ...currentDraft,
                    savedAmountAtLastSync: Math.max(0, amount),
                  }))
                }
              />
            )}
          </WizardField>

          <View>
            <Text
              style={{
                display: 'block',
                color: theme.pageTextSubdued,
              }}
            >
              {linkedBudgetCategory
                ? t('Linked category balance: {{amount}}', {
                    amount: formatPlanningAmount(linkedBudgetCategory.balance),
                  })
                : t('Link a budget category to keep this goal in sync.')}
            </Text>
            {isLoanPayoffGoal ? (
              <Text
                style={{
                  display: 'block',
                  marginTop: 6,
                  color: theme.pageTextSubdued,
                }}
              >
                {draft.payoffAccountId
                  ? remainingPayoffBalance != null
                    ? t('Current loan balance: {{amount}} owed.', {
                        amount: formatPlanningAmount(remainingPayoffBalance),
                      })
                    : t('Current loan balance will appear here.')
                  : t('Link a loan account for live payoff progress.')}
              </Text>
            ) : null}
            {selectedAccountIds.length > 0 ? (
              <Text
                style={{
                  display: 'block',
                  marginTop: 6,
                  color:
                    !isLoanPayoffGoal && accountCoverageDifference > 0
                      ? theme.warningTextDark
                      : theme.pageTextSubdued,
                }}
              >
                {!isLoanPayoffGoal && accountCoverageDifference > 0
                  ? t(
                      'Selected accounts are {{amount}} short of covering this goal today.',
                      {
                        amount: formatPlanningAmount(accountCoverageDifference),
                      },
                    )
                  : t(
                      isLoanPayoffGoal
                        ? 'Selected payment accounts currently hold {{amount}}.'
                        : 'Selected accounts currently hold {{amount}}.',
                      {
                        amount: formatPlanningAmount(linkedAccountsBalance),
                      },
                    )}
              </Text>
            ) : null}
          </View>
        </View>

        {!isNarrowWidth ? (
          <View
            style={{
              marginTop: 16,
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 28px',
              gap: 12,
              alignItems: 'end',
            }}
          >
            <Text
              style={{
                display: 'block',
                fontSize: 13,
                color: theme.tableText,
              }}
            >
              {t('Saved in')}
            </Text>
            <View />
          </View>
        ) : null}

        <View style={{ marginTop: isNarrowWidth ? 16 : 8, gap: 12 }}>
          {draft.allocations.map(allocation => (
            <AllocationRow
              key={allocation.id}
              accounts={accounts}
              accountsById={accountsById}
              allocation={allocation}
              formatPlanningAmount={formatPlanningAmount}
              otherSelectedAccountIds={draft.allocations
                .filter(item => item.id !== allocation.id)
                .map(item => item.accountId)
                .filter(accountId => accountId.length > 0)
                .concat(draft.payoffAccountId ? [draft.payoffAccountId] : [])}
              onChange={nextAllocation =>
                onChangeDraft(currentDraft => ({
                  ...currentDraft,
                  allocations: currentDraft.allocations.map(item =>
                    item.id === nextAllocation.id ? nextAllocation : item,
                  ),
                }))
              }
              onRemove={allocationId =>
                onChangeDraft(currentDraft => ({
                  ...currentDraft,
                  allocations: currentDraft.allocations.filter(
                    item => item.id !== allocationId,
                  ),
                }))
              }
            />
          ))}
        </View>

        <View
          style={{
            marginTop: 16,
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <Button
            variant="normal"
            onPress={() =>
              onChangeDraft(currentDraft => ({
                ...currentDraft,
                allocations: [
                  ...currentDraft.allocations,
                  createPlanningGoalAllocationDraft(),
                ],
              }))
            }
          >
            <View
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
            >
              <SvgAdd style={{ width: 12, height: 12 }} />
              <Text>{t('Add account')}</Text>
            </View>
          </Button>

          <View style={{ textAlign: isNarrowWidth ? 'left' : 'right' }}>
            <Text
              style={{
                display: 'block',
                fontSize: 12,
                textTransform: 'uppercase',
                color: theme.pageTextSubdued,
              }}
            >
              {isLoanPayoffGoal ? t('Current paid off') : t('Current saved')}
            </Text>
            <FinancialText
              style={{
                display: 'block',
                marginTop: 4,
                fontSize: 24,
                fontWeight: 700,
              }}
            >
              {formatPlanningAmount(savedAmount)}
            </FinancialText>
          </View>
        </View>
      </View>
    </View>
  );
}

function GoalTimelineStep({
  annualPercentageRate,
  currentMonth,
  draft,
  formatPlanningAmount,
  goalRecommendation,
  isLoanPayoffGoal,
  onChangeDraft,
  projection,
  savedAmount,
  leftToSave,
}: {
  annualPercentageRate: number;
  currentMonth: string;
  draft: PlanningGoalDraft;
  formatPlanningAmount: (value: number) => string;
  goalRecommendation: ReturnType<typeof getPlanningGoalRecommendation> | null;
  isLoanPayoffGoal: boolean;
  onChangeDraft: React.Dispatch<React.SetStateAction<PlanningGoalDraft>>;
  projection: Array<{ month: string; amount: number }>;
  savedAmount: number;
  leftToSave: number;
}) {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const targetMonthParts = draft.targetMonth.split('-');
  const selectedYear = targetMonthParts[0] ?? monthUtils.currentYear();
  const selectedMonth = targetMonthParts[1] ?? currentMonth.slice(5, 7);
  const yearStart = Math.min(
    parseInt(monthUtils.currentYear(), 10),
    parseInt(selectedYear, 10),
  );

  const monthOptions = Array.from({ length: 12 }).map((_, monthIndex) => {
    const monthValue = `${monthIndex + 1}`.padStart(2, '0');
    const monthLabel = monthUtils.format(
      `${selectedYear}-${monthValue}`,
      'LLL',
    );

    return [monthValue, monthLabel] as const;
  });

  const yearOptions = Array.from({ length: 25 }).map((_, index) => {
    const year = String(yearStart + index);
    return [year, year] as const;
  });

  const guidance = getPlanningGuidance({
    recommendation: goalRecommendation,
    t,
    formatPlanningAmount,
  });

  return (
    <View
      style={{
        display: 'grid',
        gridTemplateColumns: isNarrowWidth
          ? '1fr'
          : 'minmax(280px, 0.95fr) minmax(320px, 1.05fr)',
        gap: 18,
      }}
    >
      <View>
        <Text
          style={{
            display: 'block',
            fontSize: 20,
            fontWeight: 600,
          }}
        >
          {t('Target goal date')}
        </Text>

        <View style={{ marginTop: 12, flexDirection: 'row', gap: 8 }}>
          <Button
            variant={draft.hasTargetDate ? 'menuSelected' : 'menu'}
            onPress={() =>
              onChangeDraft(currentDraft => ({
                ...currentDraft,
                hasTargetDate: true,
              }))
            }
          >
            {t('Set')}
          </Button>
          <Button
            variant={!draft.hasTargetDate ? 'menuSelected' : 'menu'}
            onPress={() =>
              onChangeDraft(currentDraft => ({
                ...currentDraft,
                hasTargetDate: false,
              }))
            }
          >
            {t("Don't set")}
          </Button>
        </View>

        <View
          style={{
            marginTop: 16,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 12,
          }}
        >
          <WizardField label={t('Month')}>
            <Select
              value={selectedMonth}
              disabled={!draft.hasTargetDate}
              options={monthOptions}
              onChange={month =>
                onChangeDraft(currentDraft => ({
                  ...currentDraft,
                  targetMonth: `${selectedYear}-${month}`,
                }))
              }
              style={{ width: '100%' }}
            />
          </WizardField>

          <WizardField label={t('Year')}>
            <Select
              value={selectedYear}
              disabled={!draft.hasTargetDate}
              options={yearOptions}
              onChange={year =>
                onChangeDraft(currentDraft => ({
                  ...currentDraft,
                  targetMonth: `${year}-${selectedMonth}`,
                }))
              }
              style={{ width: '100%' }}
            />
          </WizardField>
        </View>

        <View
          style={{
            marginTop: 16,
            padding: 14,
            borderRadius: 6,
            backgroundColor: theme.tableBackground,
            border: `1px solid ${theme.tableBorder}`,
          }}
        >
          <Text
            style={{
              display: 'block',
              color: theme.pageTextSubdued,
            }}
          >
            {isLoanPayoffGoal
              ? t('Monthly payment')
              : t('Monthly contribution')}
          </Text>
          <FinancialText
            style={{
              display: 'block',
              marginTop: 8,
              fontSize: 30,
              fontWeight: 700,
            }}
          >
            {draft.hasTargetDate &&
            goalRecommendation?.monthlyContribution != null
              ? formatPlanningAmount(goalRecommendation.monthlyContribution)
              : t('Flexible')}
          </FinancialText>
          {annualPercentageRate > 0 ? (
            <Text
              style={{
                display: 'block',
                marginTop: 8,
                color: theme.pageTextSubdued,
              }}
            >
              {t(
                isLoanPayoffGoal
                  ? 'Includes estimated interest at {{rate}}% APR.'
                  : 'Includes estimated growth at {{rate}}% APR.',
                {
                  rate: formatApr(annualPercentageRate),
                },
              )}
            </Text>
          ) : null}
        </View>

        <View
          style={{
            marginTop: 16,
            padding: '10px 12px',
            borderRadius: 6,
            backgroundColor: guidance.backgroundColor,
            color: guidance.textColor,
          }}
        >
          <Text style={{ color: 'inherit', fontWeight: 600 }}>
            {guidance.text}
          </Text>
        </View>
      </View>

      <View
        style={{
          padding: 16,
          borderRadius: 6,
          backgroundColor: theme.tableBackground,
          border: `1px solid ${theme.tableBorder}`,
        }}
      >
        <View
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 10,
          }}
        >
          <TimelineMetric
            label={isLoanPayoffGoal ? t('Payoff target') : t('Goal amount')}
            value={formatPlanningAmount(draft.targetAmount)}
          />
          <TimelineMetric
            label={isLoanPayoffGoal ? t('Paid off so far') : t('Saved so far')}
            value={formatPlanningAmount(savedAmount)}
          />
          <TimelineMetric
            label={isLoanPayoffGoal ? t('Left to pay off') : t('Left to save')}
            value={formatPlanningAmount(leftToSave)}
          />
        </View>

        <View style={{ marginTop: 18 }}>
          <GoalProjectionChart
            projection={projection}
            targetAmount={draft.targetAmount}
          />
        </View>
      </View>
    </View>
  );
}

function AllocationRow({
  accounts,
  accountsById,
  allocation,
  formatPlanningAmount,
  otherSelectedAccountIds,
  onChange,
  onRemove,
}: {
  accounts: AccountEntity[];
  accountsById: Map<string, AccountEntity>;
  allocation: PlanningGoalDraftAllocation;
  formatPlanningAmount: (value: number) => string;
  otherSelectedAccountIds: string[];
  onChange: (allocation: PlanningGoalDraftAllocation) => void;
  onRemove: (allocationId: string) => void;
}) {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const accountOptions = accounts
    .filter(account => !account.closed)
    .map(account => [account.id, account.name] as const);

  return (
    <View
      style={{
        display: 'grid',
        gridTemplateColumns: isNarrowWidth ? '1fr' : 'minmax(0, 1fr) auto',
        gap: 12,
        alignItems: 'end',
      }}
    >
      <View>
        {isNarrowWidth ? <FormLabel title={t('Saved in')} /> : null}
        <Select
          value={allocation.accountId}
          defaultLabel={t('Select an account')}
          options={accountOptions}
          disabledKeys={otherSelectedAccountIds}
          onChange={accountId =>
            onChange({
              ...allocation,
              accountId,
            })
          }
          style={{ width: '100%' }}
        />
        <Text
          style={{
            display: 'block',
            marginTop: 6,
            color: theme.pageTextSubdued,
          }}
        >
          <AccountBalanceHint
            accountId={allocation.accountId}
            accountsById={accountsById}
            formatPlanningAmount={formatPlanningAmount}
          />
        </Text>
      </View>

      <Button
        variant="bare"
        aria-label={t('Remove account')}
        onPress={() => onRemove(allocation.id)}
        style={{
          padding: 8,
          justifySelf: isNarrowWidth ? 'flex-start' : 'center',
        }}
      >
        <SvgTrash style={{ width: 14, height: 14 }} />
      </Button>
    </View>
  );
}

function GoalProjectionChart({
  projection,
  targetAmount,
}: {
  projection: Array<{ month: string; amount: number }>;
  targetAmount: number;
}) {
  const { t } = useTranslation();

  if (projection.length === 0 || targetAmount <= 0) {
    return (
      <View
        style={{
          minHeight: 220,
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.pageTextSubdued,
        }}
      >
        <Text style={{ color: 'inherit' }}>
          {t('Set a target date to preview the path to this goal.')}
        </Text>
      </View>
    );
  }

  const chartHeight = 220;
  const chartWidth = 420;
  const left = 30;
  const right = 16;
  const top = 14;
  const bottom = 34;
  const plotWidth = chartWidth - left - right;
  const plotHeight = chartHeight - top - bottom;
  const highestAmount = Math.max(
    targetAmount,
    ...projection.map(point => point.amount),
  );

  const pointCoordinates = projection.map((point, index) => {
    const x =
      left +
      (projection.length === 1
        ? plotWidth / 2
        : (plotWidth / (projection.length - 1)) * index);
    const y =
      top +
      plotHeight -
      (highestAmount === 0 ? 0 : (point.amount / highestAmount) * plotHeight);

    return {
      ...point,
      x,
      y,
    };
  });

  const linePath = pointCoordinates
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
    )
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      <line
        x1={left}
        x2={chartWidth - right}
        y1={chartHeight - bottom}
        y2={chartHeight - bottom}
        stroke={theme.tableBorder}
        strokeWidth="1"
      />
      <line
        x1={left}
        x2={left}
        y1={top}
        y2={chartHeight - bottom}
        stroke={theme.tableBorder}
        strokeWidth="1"
      />
      <path
        d={linePath}
        fill="none"
        stroke={theme.noticeText}
        strokeWidth="3"
        strokeDasharray="6 4"
      />
      {pointCoordinates.map(point => (
        <g key={point.month}>
          <circle
            cx={point.x}
            cy={point.y}
            r="5"
            fill={theme.tableBackground}
            stroke={theme.reportsBlue}
            strokeWidth="3"
          />
          <text
            x={point.x}
            y={chartHeight - 10}
            textAnchor="middle"
            fill={theme.pageTextSubdued}
            fontSize="12"
          >
            {monthUtils.format(point.month, 'LLL yyyy')}
          </text>
        </g>
      ))}
    </svg>
  );
}

function AccountBalanceHint({
  accountId,
  accountsById,
  formatPlanningAmount,
}: {
  accountId: string;
  accountsById: Map<string, AccountEntity>;
  formatPlanningAmount: (value: number) => string;
}) {
  const { t } = useTranslation();
  const selectedAccount = accountId ? accountsById.get(accountId) : null;
  const balanceValue = useSheetValue<'account', 'balance'>(
    accountBalance(accountId),
  );

  if (!selectedAccount || balanceValue == null) {
    return t('Current balance will appear here.');
  }

  return t('Balance {{amount}}', {
    amount: formatPlanningAmount(balanceValue),
  });
}

function TimelineMetric({ label, value }: { label: string; value: string }) {
  return (
    <View>
      <Text
        style={{
          display: 'block',
          fontSize: 12,
          color: theme.pageTextSubdued,
        }}
      >
        {label}
      </Text>
      <FinancialText
        style={{
          display: 'block',
          marginTop: 4,
          fontWeight: 700,
        }}
      >
        {value}
      </FinancialText>
    </View>
  );
}

function WizardField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <View>
      <FormLabel title={label} />
      {children}
    </View>
  );
}

function createPlanningGoalDraft({
  categorySummary,
  goal,
  currentMonth,
}: {
  categorySummary?: PlanningBudgetCategorySummary;
  goal: PlanningGoal | null;
  currentMonth: string;
}): PlanningGoalDraft {
  if (goal) {
    return {
      id: goal.id,
      kind: goal.kind,
      name: goal.name,
      targetAmount: goal.targetAmount,
      payoffAccountId: goal.payoffAccountId ?? '',
      savedAmountAtLastSync: getPlanningGoalSavedAmount(goal, categorySummary),
      annualPercentageRate:
        goal.annualPercentageRate > 0 ? String(goal.annualPercentageRate) : '',
      categoryId: goal.categoryId ?? '',
      hasTargetDate: goal.targetMonth != null,
      targetMonth: goal.targetMonth ?? monthUtils.addMonths(currentMonth, 12),
      allocations: goal.allocations.map(allocation => ({
        id: allocation.id,
        accountId: allocation.accountId,
      })),
      createdAt: goal.createdAt,
    };
  }

  return {
    id: null,
    kind: null,
    name: '',
    targetAmount: 0,
    payoffAccountId: '',
    savedAmountAtLastSync: 0,
    annualPercentageRate: '',
    categoryId: '',
    hasTargetDate: true,
    targetMonth: monthUtils.addMonths(currentMonth, 12),
    allocations: [createPlanningGoalAllocationDraft()],
    createdAt: null,
  };
}

function createPlanningGoalAllocationDraft(): PlanningGoalDraftAllocation {
  return {
    id: crypto.randomUUID(),
    accountId: '',
  };
}

function parseAnnualPercentageRate(value: string) {
  const parsedRate = Number.parseFloat(value);
  return Number.isFinite(parsedRate) ? Math.max(0, parsedRate) : 0;
}

function getPlanningGuidance({
  recommendation,
  t,
  formatPlanningAmount,
}: {
  recommendation: ReturnType<typeof getPlanningGoalRecommendation> | null;
  t: ReturnType<typeof useTranslation>['t'];
  formatPlanningAmount: (value: number) => string;
}) {
  switch (recommendation?.status) {
    case 'funded':
      return {
        text: t('This goal is already funded.'),
        backgroundColor: theme.noticeBackgroundLight,
        textColor: theme.noticeTextDark,
      };
    case 'on-track':
      return {
        text: t("You're all set to reach this goal on time."),
        backgroundColor: theme.noticeBackgroundLight,
        textColor: theme.noticeTextDark,
      };
    case 'below-target':
      return {
        text:
          recommendation.monthlyContribution != null
            ? t('Budget {{amount}} this month to stay on track.', {
                amount: formatPlanningAmount(
                  recommendation.monthlyContribution,
                ),
              })
            : t('This goal needs a little more this month.'),
        backgroundColor: theme.warningBackground,
        textColor: theme.warningTextDark,
      };
    case 'budget-this-month':
      return {
        text:
          recommendation.monthlyContribution != null
            ? t('Budget {{amount}} this month to get this goal moving.', {
                amount: formatPlanningAmount(
                  recommendation.monthlyContribution,
                ),
              })
            : t('Budget this goal this month to get started.'),
        backgroundColor: theme.warningBackground,
        textColor: theme.warningTextDark,
      };
    case 'missing-category':
      return {
        text: t(
          'Link a budget category so this goal can stay tied to your month.',
        ),
        backgroundColor: theme.errorBackground,
        textColor: theme.errorTextDark,
      };
    case 'flexible':
    default:
      return {
        text: t(
          'Leave the date open if this goal does not need a monthly pace yet.',
        ),
        backgroundColor: theme.tableBackground,
        textColor: theme.pageTextSubdued,
      };
  }
}

function formatApr(rate: number) {
  return rate.toFixed(2).replace(/\.?0+$/, '');
}
